/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { ref, watch } from 'vue'

const THEME_KEY = 'theme'
const isDark = ref(true)

function apply(val) {
  document.documentElement.setAttribute('data-theme', val ? 'dark' : 'light')
}

function load() {
  // User override stored in localStorage
  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'dark') return true
    if (stored === 'light') return false
  } catch {}

  // Default based on environment
  if (import.meta.env.MODE === 'development') {
    // Dev -> DARK
    return true
  }

  // 3) Prod -> LIGHT
  if (import.meta.env.MODE === 'production') {
    return false
  }

  // Fallback (should never trigger)
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false
}

export function initTheme() {
  isDark.value = load()
  apply(isDark.value)
}

watch(isDark, (val) => {
  apply(val)
  try {
    localStorage.setItem(THEME_KEY, val ? 'dark' : 'light')
  } catch {}
})

export function useTheme() {
  return { isDark }
}
